import React, { useEffect, useMemo, useState } from "react";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ClearIcon from "@mui/icons-material/Clear";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import Popover from "@mui/material/Popover";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import dayjs from "dayjs";
import {
  JALALI_MONTH_NAMES,
  formatJalaliMonthParts,
  formatJalaliParts,
  formatJalaliInputDate,
  formatJalaliInputDateTime,
  formatJalaliInputMonth,
  getJalaliMonthLength,
  jalaliToGregorianParts,
  parseJalaliDateTimeToGregorian,
  parseJalaliDateToGregorian,
  parseJalaliMonthToGregorian,
} from "../utils/dateFormat";

const WEEK_DAYS = ["ش", "ی", "د", "س", "چ", "پ", "ج"];

const MODE_CONFIG = {
  date: {
    placeholder: "1405/03/13",
    format: formatJalaliInputDate,
    parse: parseJalaliDateToGregorian,
    helper: "YYYY/MM/DD",
  },
  datetime: {
    placeholder: "1405/03/13 15:07",
    format: formatJalaliInputDateTime,
    parse: parseJalaliDateTimeToGregorian,
    helper: "YYYY/MM/DD HH:mm",
  },
  month: {
    placeholder: "1405/03",
    format: formatJalaliInputMonth,
    parse: parseJalaliMonthToGregorian,
    helper: "YYYY/MM",
  },
};

const pad2 = (value) => String(value).padStart(2, "0");

const parsePickerParts = (displayValue) => {
  const match = String(displayValue || "").match(/^(\d{4})\/(\d{2})(?:\/(\d{2}))?/);
  if (!match) return null;
  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3] || 1),
  };
};

const getTodayJalaliParts = () => parsePickerParts(formatJalaliInputDate(new Date()));

const getTimePartsFromValue = (value) => {
  const match = String(value || "").match(/(?:T|\s)(\d{1,2}):(\d{2})/);
  if (!match) return { hour: 0, minute: 0 };
  return {
    hour: Number(match[1]),
    minute: Number(match[2]),
  };
};

const toGregorianDateValue = (year, month, day) => {
  const gregorian = jalaliToGregorianParts(year, month, day);
  return `${gregorian.year}-${pad2(gregorian.month)}-${pad2(gregorian.day)}`;
};

const toGregorianDateTimeValue = (year, month, day, hour, minute) => {
  return `${toGregorianDateValue(year, month, day)}T${pad2(hour)}:${pad2(minute)}`;
};

const toGregorianMonthValue = (year, month) => {
  return parseJalaliMonthToGregorian(formatJalaliMonthParts(year, month));
};

const emitTextFieldChange = (onChange, value, name) => {
  onChange?.({ target: { name, value } });
};

export function JalaliDateField({
  mode = "date",
  value,
  onChange,
  helperText,
  error,
  inputProps,
  InputProps,
  name,
  disabled,
  ...props
}) {
  const config = MODE_CONFIG[mode] || MODE_CONFIG.date;
  const displayValue = config.format(value);
  const selectedParts = useMemo(() => parsePickerParts(displayValue), [displayValue]);
  const todayParts = useMemo(() => getTodayJalaliParts(), []);
  const [anchorEl, setAnchorEl] = useState(null);
  const [viewYear, setViewYear] = useState(selectedParts?.year || todayParts.year);
  const [viewMonth, setViewMonth] = useState(selectedParts?.month || todayParts.month);
  const [timeParts, setTimeParts] = useState(() => getTimePartsFromValue(value));
  const isOpen = Boolean(anchorEl);
  const monthLength = getJalaliMonthLength(viewYear, viewMonth);
  const firstGregorian = jalaliToGregorianParts(viewYear, viewMonth, 1);
  const firstWeekday = (new Date(firstGregorian.year, firstGregorian.month - 1, firstGregorian.day).getDay() + 1) % 7;
  const calendarCells = [
    ...Array(firstWeekday).fill(null),
    ...Array.from({ length: monthLength }, (_, index) => index + 1),
  ];

  useEffect(() => {
    const nextParts = parsePickerParts(config.format(value));
    if (nextParts) {
      setViewYear(nextParts.year);
      setViewMonth(nextParts.month);
    }
    setTimeParts(getTimePartsFromValue(value));
  }, [config, value]);

  const openPicker = (event) => {
    if (disabled) return;
    setAnchorEl(event.currentTarget);
  };

  const closePicker = () => {
    setAnchorEl(null);
  };

  const changeMonth = (amount) => {
    setViewMonth((currentMonth) => {
      const nextMonth = currentMonth + amount;
      if (nextMonth < 1) {
        setViewYear((currentYear) => currentYear - 1);
        return 12;
      }
      if (nextMonth > 12) {
        setViewYear((currentYear) => currentYear + 1);
        return 1;
      }
      return nextMonth;
    });
  };

  const changeYear = (amount) => {
    setViewYear((currentYear) => currentYear + amount);
  };

  const selectDay = (day) => {
    const nextValue =
      mode === "datetime"
        ? toGregorianDateTimeValue(viewYear, viewMonth, day, timeParts.hour, timeParts.minute)
        : toGregorianDateValue(viewYear, viewMonth, day);
    emitTextFieldChange(onChange, nextValue, name);
    closePicker();
  };

  const selectMonth = (month) => {
    emitTextFieldChange(onChange, toGregorianMonthValue(viewYear, month), name);
    closePicker();
  };

  const clearValue = (event) => {
    event.stopPropagation();
    emitTextFieldChange(onChange, "", name);
    closePicker();
  };

  const handleTimeChange = (part, nextValue) => {
    const nextTimeParts = {
      ...timeParts,
      [part]: Number(nextValue),
    };
    setTimeParts(nextTimeParts);

    if (selectedParts) {
      emitTextFieldChange(
        onChange,
        toGregorianDateTimeValue(
          selectedParts.year,
          selectedParts.month,
          selectedParts.day,
          nextTimeParts.hour,
          nextTimeParts.minute
        ),
        name
      );
    }
  };

  return (
    <>
      <TextField
        {...props}
        name={name}
        disabled={disabled}
        type="text"
        value={displayValue}
        onClick={openPicker}
        placeholder={props.placeholder || config.placeholder}
        error={Boolean(error)}
        helperText={helperText || config.helper}
        inputProps={{
          ...inputProps,
          readOnly: true,
          dir: "ltr",
          style: {
            ...(inputProps?.style || {}),
            cursor: disabled ? "default" : "pointer",
            textAlign: "left",
          },
        }}
        InputProps={{
          ...InputProps,
          endAdornment: (
            <>
              {displayValue && !disabled ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={clearValue} edge="end" aria-label="Clear date">
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null}
              <InputAdornment position="end">
                <IconButton size="small" onClick={openPicker} edge="end" aria-label="Open Jalali calendar">
                  {mode === "datetime" ? <AccessTimeIcon fontSize="small" /> : <CalendarMonthIcon fontSize="small" />}
                </IconButton>
              </InputAdornment>
              {InputProps?.endAdornment}
            </>
          ),
        }}
      />
      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={closePicker}
        disableRestoreFocus
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Box sx={{ width: mode === "month" ? 292 : 320, p: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
            <IconButton size="small" onClick={() => changeMonth(-1)} disabled={mode === "month"}>
              <ChevronRightIcon fontSize="small" />
            </IconButton>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton size="small" onClick={() => changeYear(-1)}>
                <ChevronRightIcon fontSize="small" />
              </IconButton>
              <Typography variant="subtitle1" sx={{ minWidth: 128, textAlign: "center", fontWeight: 600 }}>
                {mode === "month" ? viewYear : `${JALALI_MONTH_NAMES[viewMonth - 1]} ${viewYear}`}
              </Typography>
              <IconButton size="small" onClick={() => changeYear(1)}>
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
            </Box>
            <IconButton size="small" onClick={() => changeMonth(1)} disabled={mode === "month"}>
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
          </Box>

          {mode === "month" ? (
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1 }}>
              {JALALI_MONTH_NAMES.map((monthName, index) => {
                const month = index + 1;
                const isSelected = selectedParts?.year === viewYear && selectedParts?.month === month;
                return (
                  <Button
                    key={monthName}
                    variant={isSelected ? "contained" : "outlined"}
                    onClick={() => selectMonth(month)}
                    sx={{ minHeight: 42 }}
                  >
                    {monthName}
                  </Button>
                );
              })}
            </Box>
          ) : (
            <>
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0.5, mb: 0.5 }}>
                {WEEK_DAYS.map((dayName) => (
                  <Typography key={dayName} variant="caption" sx={{ textAlign: "center", color: "text.secondary" }}>
                    {dayName}
                  </Typography>
                ))}
              </Box>
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0.5 }}>
                {calendarCells.map((day, index) =>
                  day ? (
                    <Button
                      key={`${viewYear}-${viewMonth}-${day}`}
                      variant={
                        selectedParts?.year === viewYear &&
                        selectedParts?.month === viewMonth &&
                        selectedParts?.day === day
                          ? "contained"
                          : "text"
                      }
                      onClick={() => selectDay(day)}
                      sx={{ minWidth: 0, width: 38, height: 34, p: 0 }}
                    >
                      {day}
                    </Button>
                  ) : (
                    <Box key={`blank-${index}`} sx={{ width: 38, height: 34 }} />
                  )
                )}
              </Box>
            </>
          )}

          {mode === "datetime" ? (
            <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
              <TextField
                select
                size="small"
                label="Hour"
                value={timeParts.hour}
                onChange={(event) => handleTimeChange("hour", event.target.value)}
                sx={{ flex: 1 }}
              >
                {Array.from({ length: 24 }, (_, hour) => (
                  <MenuItem key={hour} value={hour}>
                    {pad2(hour)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Minute"
                value={timeParts.minute}
                onChange={(event) => handleTimeChange("minute", event.target.value)}
                sx={{ flex: 1 }}
              >
                {Array.from({ length: 60 }, (_, minute) => (
                  <MenuItem key={minute} value={minute}>
                    {pad2(minute)}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          ) : null}
        </Box>
      </Popover>
    </>
  );
}

export function JalaliDatePickerField({ value, onChange, label, slotProps = {}, ...props }) {
  const textFieldProps = slotProps.textField || {};
  const gregorianValue = value?.format ? value.format("YYYY-MM-DD") : "";

  return (
    <JalaliDateField
      {...props}
      {...textFieldProps}
      label={label}
      value={gregorianValue}
      onChange={(event) => {
        const nextValue = event.target.value;
        onChange?.(nextValue ? dayjs(nextValue) : null);
      }}
    />
  );
}

export default JalaliDateField;
